import { createContext, useContext, useEffect, useRef, useState, ReactNode } from "react";
import { validateContrast, getCanonicalDomain } from "@guardian/shared-utils";
import * as Sentry from "@sentry/react";
import { apiClient } from "@/services/api/client";
import { extractBrandSlug, hexToHslComponents } from "@/lib/branding";
import { BrandHydrationFallback } from "./BrandHydrationFallback";

export interface BrandBlock {
  primaryColor: string;
  secondaryColor: string;
  textOnPrimaryColor: string;
  displayName: string;
  logoUrl: string;
  slug: string;
  headerStyle?: "SOLID" | "GRADIENT" | null;
  aboutUrl?: string | null;
  contactMode?: "LINK" | "DETAILS" | null;
  contactUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  contactWhatsapp?: string | null;
  contactAddress?: string | null;
  socialFacebook?: string | null;
  socialInstagram?: string | null;
  socialTwitter?: string | null;
  socialLinkedin?: string | null;
  socialTiktok?: string | null;
  floatingWhatsappEnabled?: boolean | null;
}

interface BrandingContextValue {
  branding: BrandBlock | null;
  isLoading: boolean;
  isBranded: boolean;
  isRedirecting: boolean;
}

// WHY exported: PreviewBrandingProvider re-supplies this context so children that
// already read brand via useBranding (MainHeader, MainFooter, GuestCheckinPage) get
// the postMessage-driven brand without learning a second hook.
export const BrandingContext = createContext<BrandingContextValue>({
  branding: null,
  isLoading: true,
  isBranded: false,
  isRedirecting: false,
});

export const useBranding = () => useContext(BrandingContext);

function hasActiveBrandFields(payload: unknown): payload is BrandBlock {
  if (typeof payload !== "object" || payload === null) return false;
  const p = payload as Record<string, unknown>;
  return (
    typeof p.primaryColor === "string" &&
    typeof p.secondaryColor === "string" &&
    typeof p.textOnPrimaryColor === "string" &&
    typeof p.displayName === "string" &&
    typeof p.logoUrl === "string" &&
    typeof p.slug === "string"
  );
}

const REDIRECT_KINDS = new Set(["SLUG_CHANGED", "TIER_DOWNGRADED", "ADMIN_REVOKED"]);
const SLUG_PATTERN = /^[a-z0-9-]{3,40}$/;

function hasRedirectFields(
  payload: unknown,
): payload is { redirectTo: string | null; redirectKind: string } {
  if (typeof payload !== "object" || payload === null) return false;
  const p = payload as Record<string, unknown>;
  if (typeof p.redirectKind !== "string") return false;
  if (!REDIRECT_KINDS.has(p.redirectKind as string)) return false;
  // # WHY validate slug shape at the SPA: window.location.replace target is interpolated
  // from redirectTo. The DB CHECK constraint prevents writing arbitrary values today, but
  // the SPA must not trust the wire — a future DB-bypass path that wrote
  // redirectTo="evil.com?x=" would interpolate to https://evil.com?x=.gcheckin.com/... (open redirect).
  if (p.redirectTo === null) return true;
  return typeof p.redirectTo === "string" && SLUG_PATTERN.test(p.redirectTo);
}

// WHY: --secondary-foreground is a FIXED near-white in BOTH :root and .dark
// (210 40% 98%, i.e. #F8FAFC -- see src/index.css) while --secondary below is written from
// the owner's brand. Nothing paired them, so a light brand secondary put near-white text on a
// near-white surface. Keep this hex in lockstep with --secondary-foreground in src/index.css.
const SECONDARY_FOREGROUND_HEX = "#F8FAFC";

export type ApplyBrandOutcome = "applied" | "rejected_contrast" | "rejected_invalid_hex";

// WHY exported: PreviewBrandingProvider (multi-viewport live preview) reuses this
// exact code path to avoid drift between the production fetch-driven render and the
// postMessage-driven preview render. Keep CSS-var writes, title/favicon updates,
// and the header-style dataset hook in ONE place.
export function applyBrand(brand: BrandBlock): ApplyBrandOutcome {
  let contrast;
  let secondaryContrast;
  try {
    contrast = validateContrast(brand.primaryColor, brand.textOnPrimaryColor);
    // WHY in this try: brand.secondaryColor was previously parsed only by hexToHslComponents,
    // below and OUTSIDE any catch, so a malformed secondary hex threw straight out of
    // applyBrand. isBrandBlock only checks that it is a string. Parsing it here folds it into
    // the existing rejected_invalid_hex contract instead.
    secondaryContrast = validateContrast(brand.secondaryColor, SECONDARY_FOREGROUND_HEX);
  } catch (e) {
    console.warn(
      `BrandingProvider: dropping brand for slug "${brand.slug}" — invalid hex (${
        e instanceof Error ? e.message : String(e)
      })`,
    );
    return "rejected_invalid_hex";
  }
  if (!contrast.pass) {
    console.warn(
      `BrandingProvider: dropping brand for slug "${brand.slug}" — contrast ${contrast.roundedRatio}:1 < 4.5:1`,
    );
    return "rejected_contrast";
  }
  const root = document.documentElement.style;
  root.setProperty("--brand-primary", brand.primaryColor);
  root.setProperty("--brand-secondary", brand.secondaryColor);
  root.setProperty("--brand-text-on-primary", brand.textOnPrimaryColor);
  const primaryHsl = hexToHslComponents(brand.primaryColor);
  root.setProperty("--primary", primaryHsl);
  root.setProperty("--ring", primaryHsl);
  // WHY only the token, not the whole brand: --brand-secondary (set above) is the owner's
  // colour for branded surfaces that supply their own foreground, and a pale secondary is a
  // legitimate brand choice there. --secondary is the ShadCN token whose foreground the owner
  // cannot influence, so it is the only thing that has to fall back. Dropping the entire brand
  // over it would cost the owner their primary colour, logo and title for a token that is
  // barely load-bearing.
  // WHY removeProperty on failure: applyBrand is re-entrant -- PreviewBrandingProvider calls it
  // again on every postMessage -- so a stale inline --secondary from a previous brand would
  // survive and keep shadowing the .dark block.
  if (secondaryContrast.pass) {
    root.setProperty("--secondary", hexToHslComponents(brand.secondaryColor));
  } else {
    root.removeProperty("--secondary");
    console.warn(
      `BrandingProvider: keeping theme --secondary for slug "${brand.slug}" — brand secondary ` +
        `contrast ${secondaryContrast.roundedRatio}:1 < 4.5:1 against --secondary-foreground`,
    );
  }
  document.documentElement.dataset.headerStyle = brand.headerStyle ?? "SOLID";
  document.title = brand.displayName;
  let icon = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
  if (!icon) {
    icon = document.createElement("link");
    icon.rel = "icon";
    icon.type = "image/png";
    document.head.appendChild(icon);
  }
  icon.href = brand.logoUrl;
  return "applied";
}

// WHY: the API-driven path also records Sentry latency telemetry. PreviewBrandingProvider
// deliberately skips this — preview latency is meaningless against the production SLI.
function applyBranding(brand: BrandBlock, startedAtMs: number): ApplyBrandOutcome {
  const outcome = applyBrand(brand);
  if (outcome !== "applied") return outcome;
  Sentry.setMeasurement("brand_hydration_ms", performance.now() - startedAtMs, "millisecond");
  return "applied";
}

export function BrandingProvider({ children }: { children: ReactNode; language?: string }) {
  const [branding, setBranding] = useState<BrandBlock | null>(null);
  // WHY initial = (slug !== null): on the canonical domain we never fetch,
  // so children render at first paint. On a branded subdomain we start in
  // loading state until the fetch settles OR the 3 s ceiling fires.
  const [isLoading, setIsLoading] = useState(() => extractBrandSlug(window.location.hostname) !== null);
  const [isRedirecting, setIsRedirecting] = useState(false);
  // WHY: React 18 StrictMode invokes effects twice in dev. Guard against double-apply
  // so document.title/favicon aren't emitted twice.
  const appliedRef = useRef(false);
  // WHY: outcome is tagged at multiple sites (apply, malformed, fetch_error, ceiling).
  // The 3 s ceiling can fire in the same microtask as the fetch's terminal outcome;
  // without this guard the ceiling tag would overwrite the real failure signal.
  const outcomeTaggedRef = useRef(false);

  function tagOutcomeOnce(outcome: string) {
    if (outcomeTaggedRef.current) return;
    outcomeTaggedRef.current = true;
    Sentry.setTag("brand_outcome", outcome);
  }

  useEffect(() => {
    if (appliedRef.current) return;
    const slug = extractBrandSlug(window.location.hostname);
    if (!slug) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    const startedAtMs = performance.now();
    (async () => {
      try {
        // apiClient.get<T> returns T directly (it unwraps response.data internally),
        // and its request interceptor prepends `/${language}` to every URL — so this
        // path is ONLY the post-`/v1/{language}/` portion. Anything fuller would
        // double-prefix.
        const payload = await apiClient.get<unknown>(
          `/public/branding/by-slug/${encodeURIComponent(slug)}`,
        );
        if (cancelled || appliedRef.current) return;
        if (hasRedirectFields(payload)) {
          appliedRef.current = true;
          setIsRedirecting(true);
          const canonicalDomain = getCanonicalDomain();
          const target = payload.redirectTo
            ? `https://${payload.redirectTo}.${canonicalDomain}${window.location.pathname}${window.location.search}`
            : `https://${canonicalDomain}${window.location.pathname}${window.location.search}`;
          window.location.replace(target);
          return;
        }
        if (hasActiveBrandFields(payload)) {
          appliedRef.current = true;
          const outcome = applyBranding(payload, startedAtMs);
          tagOutcomeOnce(outcome);
          if (outcome === "applied") setBranding(payload);
        } else {
          tagOutcomeOnce("malformed_response");
        }
      } catch (err) {
        // WHY: apiClient's response interceptor rewrites axios errors through
        // formatApiError to ErrorApiDto { name, code, message, errorId } — there is
        // NO err.response on what callers see in the form-app. Read err.code (0 for
        // network error, HTTP status for response errors).
        Sentry.addBreadcrumb({
          category: "branding",
          level: "warning",
          message: `BrandingProvider fetch failed for slug "${slug}"`,
          data: { code: (err as { code?: number }).code },
        });
        tagOutcomeOnce("fetch_error");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // WHY effect deps = []: brand resolution is locale-independent. apiClient's
    // own language sync (via i18n.on('languageChanged')) handles the URL prefix;
    // we don't need to re-fetch on locale change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WHY 3 s ceiling: cap worst-case visible-loading window so a hung backend converts
  // to "default GCI render" rather than "permanently-blank page". Same fail-graceful
  // target as the fetch's catch branch, just time-bounded.
  useEffect(() => {
    if (!isLoading) return;
    const slug = extractBrandSlug(window.location.hostname);
    if (slug === null) return;
    const ceilingMs = 3000;
    const id = window.setTimeout(() => {
      if (appliedRef.current) return;
      tagOutcomeOnce("fallback_ceiling_hit");
      setIsLoading(false);
    }, ceilingMs);
    return () => window.clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  // WHY: render null while redirecting so children don't briefly paint with default
  // branding between fetch resolution and window.location.replace unload.
  if (isRedirecting) return null;

  const slug = extractBrandSlug(window.location.hostname);
  if (slug !== null && isLoading && !appliedRef.current) {
    return (
      <BrandingContext.Provider
        value={{ branding: null, isLoading: true, isBranded: false, isRedirecting: false }}
      >
        <BrandHydrationFallback />
      </BrandingContext.Provider>
    );
  }

  return (
    <BrandingContext.Provider
      value={{ branding, isLoading, isBranded: branding !== null, isRedirecting }}
    >
      {children}
    </BrandingContext.Provider>
  );
}
